import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from '../../../../i18n';
import './letter-storm.css';

export interface LetterStormAck {
  accepted: boolean;
  reason?: 'invalid' | 'duplicate' | 'not-in-dictionary' | 'capped';
  word: string;
  claimRank: number;
  wordPoints: number;
  totalPoints: number;
  alreadyClaimed?: boolean;
}

export interface LetterStormPlayerProps {
  roomName: string;
  letter: string;
  minWordLength?: number;
  maxWordLength?: number;
  disabled?: boolean;
  showLetter?: boolean;
  onPointsChange?: (points: number) => void;
  socket: {
    emit: (event: string, data: unknown) => void;
    on: (event: string, cb: (data: unknown) => void) => void;
    off: (event: string, cb?: (data: unknown) => void) => void;
  };
}

function normalizeWord(raw: string): string {
  return raw.trim().toLowerCase().replace(/[^a-z]/g, '');
}

type FlashKind = 'ok' | 'shared' | 'bad' | 'dup' | 'capped';

/**
 * Player view: submit one word at a time; server awards claim-rank points and updates host board.
 * Form layout matches WhosOnImage (stacked input + full-width submit button).
 */
export const LetterStormPlayer: React.FC<LetterStormPlayerProps> = ({
  roomName,
  letter,
  minWordLength = 3,
  maxWordLength = 12,
  disabled = false,
  showLetter = false,
  onPointsChange,
  socket,
}) => {
  const { t } = useTranslation();
  const displayLetter = (letter || '?').toUpperCase().slice(0, 1);
  const letterKey = displayLetter.toLowerCase();

  const [draft, setDraft] = useState('');
  const [myWords, setMyWords] = useState<Array<{ word: string; points: number; claimRank: number }>>([]);
  const [totalPoints, setTotalPoints] = useState(0);
  const [roomClaimed, setRoomClaimed] = useState<Set<string>>(() => new Set());
  const [flash, setFlash] = useState<FlashKind | null>(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const myWordsRef = useRef(myWords);
  const flashTimerRef = useRef<number | null>(null);
  myWordsRef.current = myWords;

  const canSubmit = Boolean(draft.trim()) && !disabled && !busy;

  const showFlash = useCallback((kind: FlashKind) => {
    setFlash(kind);
    if (flashTimerRef.current != null) window.clearTimeout(flashTimerRef.current);
    flashTimerRef.current = window.setTimeout(() => {
      setFlash(null);
      flashTimerRef.current = null;
    }, 900);
  }, []);

  useEffect(() => {
    if (disabled) setBusy(false);
  }, [disabled]);

  useEffect(() => {
    return () => {
      if (flashTimerRef.current != null) window.clearTimeout(flashTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const onAck = (data: unknown) => {
      const d = data as { points?: number; letterStorm?: LetterStormAck };
      if (!d?.letterStorm) return;
      setBusy(false);
      const ls = d.letterStorm;
      if (typeof d.points === 'number') {
        setTotalPoints(d.points);
        onPointsChange?.(d.points);
      }
      if (!ls.accepted) {
        showFlash(ls.reason === 'duplicate' ? 'dup' : ls.reason === 'capped' ? 'capped' : 'bad');
        return;
      }
      setMyWords((prev) => {
        if (prev.some((x) => x.word === ls.word)) return prev;
        return [...prev, { word: ls.word, points: ls.wordPoints, claimRank: ls.claimRank }];
      });
      setRoomClaimed((prev) => new Set(prev).add(ls.word));
      showFlash(ls.alreadyClaimed || ls.claimRank > 1 ? 'shared' : 'ok');
      inputRef.current?.focus();
    };

    const onBoard = (data: unknown) => {
      const d = data as { board?: Array<{ word: string }> };
      if (!Array.isArray(d?.board)) return;
      setRoomClaimed(new Set(d.board.map((b) => b.word)));
    };

    socket.on('bonus-answer-ack', onAck);
    socket.on('letter-storm-update', onBoard);
    return () => {
      socket.off('bonus-answer-ack', onAck);
      socket.off('letter-storm-update', onBoard);
    };
  }, [socket, onPointsChange, showFlash]);

  const tryAddWord = useCallback(() => {
    if (disabled || busy) return;
    const w = normalizeWord(draft);
    if (!w) return;

    if (w.length < minWordLength || w.length > maxWordLength || !w.startsWith(letterKey)) {
      showFlash('bad');
      return;
    }
    if (myWordsRef.current.some((x) => x.word === w)) {
      showFlash('dup');
      return;
    }

    setDraft('');
    setBusy(true);
    socket.emit('bonus-answer', { roomName, answer: w });
  }, [disabled, busy, draft, minWordLength, maxWordLength, letterKey, socket, roomName, showFlash]);

  const hintLines = String(t('bonus.player.letterStormHint', { letter: displayLetter })).split(
    '\n'
  );

  return (
    <div className="letter-storm-player">
      {showLetter ? (
        <div className="letter-storm-player__letter" aria-hidden>
          {displayLetter}
        </div>
      ) : null}

      <p className="bonus-player-hint letter-storm-player__hint">
        {hintLines.map((line: string, i: number) => (
          <React.Fragment key={i}>
            {line}
            {i < hintLines.length - 1 ? <br /> : null}
          </React.Fragment>
        ))}
      </p>

      <div className="letter-storm-player__form">
        <input
          ref={inputRef}
          className="letter-storm-player__input player-surface-panel"
          type="text"
          inputMode="text"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          autoComplete="off"
          autoFocus
          disabled={disabled || busy}
          value={draft}
          placeholder={t('bonus.player.letterStormPlaceholder', { letter: displayLetter })}
          aria-label={t('bonus.player.letterStormInputAria', { letter: displayLetter })}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              tryAddWord();
            }
          }}
        />
        <button
          type="button"
          className="button letter-storm-player__submit"
          disabled={!canSubmit}
          onClick={tryAddWord}
          style={{
            width: '100%',
            background: canSubmit
              ? 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)'
              : 'var(--surface-answer-disabled)',
            color: canSubmit ? 'var(--text-on-primary)' : 'var(--text-secondary)',
            border: canSubmit ? 'none' : '1px solid rgba(var(--color-secondary-rgb), 0.24)',
            opacity: 1,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
          }}
        >
          {t('gameplay.submitAnswer')}
        </button>
      </div>

      <div
        className={
          flash === 'ok' || flash === 'shared'
            ? 'letter-storm-player__flash letter-storm-player__flash--ok'
            : flash === 'bad' || flash === 'dup' || flash === 'capped'
              ? 'letter-storm-player__flash letter-storm-player__flash--bad'
              : 'letter-storm-player__flash'
        }
        role="status"
        aria-live="polite"
      >
        {flash === 'ok'
          ? t('bonus.player.letterStormFlashOk')
          : flash === 'shared'
            ? t('bonus.player.letterStormFlashShared')
            : flash === 'dup'
              ? t('bonus.player.letterStormFlashDup')
              : flash === 'capped'
                ? t('bonus.player.letterStormFlashCapped')
                : flash === 'bad'
                  ? t('bonus.player.letterStormFlashBad')
                  : '\u00a0'}
      </div>

      <div className="letter-storm-player__stats">
        <span>{t('bonus.player.letterStormWordCount', { count: myWords.length })}</span>
        <span>{t('bonus.player.letterStormPoints', { points: totalPoints })}</span>
      </div>

      <div className="letter-storm-player__chips" aria-label={t('bonus.player.letterStormWordsAria')}>
        {myWords.length === 0 ? (
          <span className="letter-storm-player__empty">{t('bonus.player.letterStormEmpty')}</span>
        ) : (
          myWords.map((entry) => (
            <span
              key={entry.word}
              className={
                entry.claimRank > 1
                  ? 'letter-storm-player__chip letter-storm-player__chip--shared'
                  : 'letter-storm-player__chip'
              }
            >
              {entry.word}
              <em>+{entry.points}</em>
            </span>
          ))
        )}
      </div>

      {roomClaimed.size > 0 ? (
        <p className="letter-storm-player__room-hint">
          {t('bonus.player.letterStormRoomClaimed', { count: roomClaimed.size })}
        </p>
      ) : null}
    </div>
  );
};
